import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Box, Button, VStack, Text, Heading } from '@chakra-ui/react';
import { clearAllCaches, forceRefresh } from '../utils/pwaUtils';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    // Clear caches when a critical error occurs
    clearAllCaches().catch(console.error);
  }

  handleReload = () => {
    forceRefresh();
  };

  handleClearCacheAndReload = async () => {
    try {
      await clearAllCaches();
      window.location.reload();
    } catch (error) {
      console.error('Failed to clear cache:', error);
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="gray.50"
          p={6}
        >
          <VStack spacing={6} textAlign="center" maxW="400px">
            <Heading size="lg" color="red.500">
              Oops! Something went wrong
            </Heading>
            <Text color="gray.600">
              The app encountered an unexpected error. This might be due to cached data conflicts.
            </Text>
            <VStack spacing={3} w="full">
              <Button
                colorScheme="blue"
                onClick={this.handleReload}
                w="full"
              >
                Reload App
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                onClick={this.handleClearCacheAndReload}
                w="full"
              >
                Clear Cache & Reload
              </Button>
            </VStack>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                bg="red.50"
                p={4}
                borderRadius="md"
                w="full"
                textAlign="left"
              >
                <Text fontSize="sm" color="red.600" fontFamily="mono">
                  {this.state.error.message}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
